import { useEffect, useState } from 'react';
import { IconEyeOff } from '@tabler/icons-react';
import {
  IconButton,
  Progress,
  Stack,
  Text,
  WP_ICON_NAV,
} from '@wapharma/design-system';
import { useGtinDumpSyncInboxContextOptional } from '../contexts/GtinDumpSyncInboxContext';
import {
  dumpSyncPhaseLabel,
  GTIN_DUMP_SYNC_STARTED_EVENT,
  type TrackedDumpSync,
} from '../hooks/useGtinDumpSyncInbox';
import { InboxDrillableHint } from './InboxDrillableHint';
import { ImportJobStatusDot } from './ImportJobStatusDot';
import styles from './InboxBulkImportItems.module.css';

const HIGHLIGHT_MS = 3500;

function DumpSyncCard({
  title,
  subtitle,
  status,
  unread,
  highlight,
  children,
  onOpen,
  onDismiss,
  dismissDisabled,
}: {
  title: string;
  subtitle?: string;
  status: TrackedDumpSync['status'];
  unread?: boolean;
  highlight?: boolean;
  children?: React.ReactNode;
  onOpen?: () => void;
  onDismiss?: () => void;
  dismissDisabled?: boolean;
}) {
  return (
    <article
      className={`${styles.card} ${unread ? styles.cardUnread : styles.cardRead} ${highlight ? styles.cardHighlight : ''}`}
      data-inbox-drillable={onOpen ? '' : undefined}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? `${title} — voir le détail` : undefined}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      style={onOpen ? { cursor: 'pointer' } : undefined}
    >
      <div className={styles.cardHead}>
        <ImportJobStatusDot
          status={status === 'RUNNING' ? 'RUNNING' : status}
          size={12}
          className={styles.cardStatus}
        />
        <div className={styles.cardTitles}>
          <Text size="sm" fw={unread ? 600 : 500} style={{ lineHeight: 1.35 }}>
            {title}
          </Text>
          {subtitle ? (
            <Text
              size="xs"
              c="dimmed"
              style={{ marginTop: 4, fontVariantNumeric: 'tabular-nums' }}
            >
              {subtitle}
            </Text>
          ) : null}
        </div>
        <div className={styles.cardActionsInline}>
          {onOpen ? <InboxDrillableHint /> : null}
          {onDismiss || dismissDisabled ? (
            <IconButton
              type="button"
              size="xs"
              variant="ghost"
              aria-label={
                dismissDisabled
                  ? 'Masquer indisponible pendant la sync'
                  : 'Masquer'
              }
              title={
                dismissDisabled
                  ? 'Sync en cours — masquage indisponible'
                  : undefined
              }
              disabled={dismissDisabled}
              onClick={
                dismissDisabled
                  ? undefined
                  : (e) => {
                      e.stopPropagation();
                      onDismiss?.();
                    }
              }
            >
              <IconEyeOff {...WP_ICON_NAV} />
            </IconButton>
          ) : null}
        </div>
      </div>
      {children ? <div className={styles.cardBody}>{children}</div> : null}
    </article>
  );
}

/** OBF dump sync progress + result — inbox cards (survives leaving Sources GTIN). */
export function InboxGtinDumpSyncItems({ onOpenDetail }: { onOpenDetail?: () => void }) {
  const dumpSync = useGtinDumpSyncInboxContextOptional();
  const [highlight, setHighlight] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onStarted = () => {
      setHighlight(true);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setHighlight(false), HIGHLIGHT_MS);
    };
    window.addEventListener(GTIN_DUMP_SYNC_STARTED_EVENT, onStarted);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(GTIN_DUMP_SYNC_STARTED_EVENT, onStarted);
    };
  }, []);

  if (!dumpSync) return null;

  const { job, inFlight, showResult, dismissResult, importProgress } = dumpSync;
  if (!job) return null;

  const showProgress = Boolean(inFlight && importProgress);

  if (!showProgress && !showResult) return null;

  return (
    <Stack gap="xs" className={styles.list} aria-label="Mise à jour Sources GTIN">
      {showProgress && importProgress ? (
        <DumpSyncCard
          title="Sources GTIN — mise à jour"
          status="RUNNING"
          unread
          highlight={highlight}
          onOpen={onOpenDetail}
          subtitle={
            importProgress.indeterminate
              ? dumpSyncPhaseLabel(importProgress.phase)
              : `${dumpSyncPhaseLabel(importProgress.phase)} · ${importProgress.current.toLocaleString('fr-FR')} / ${importProgress.total.toLocaleString('fr-FR')}`
          }
          dismissDisabled
        >
          {!importProgress.indeterminate ? (
            <Progress
              value={Math.min(importProgress.current, importProgress.total || 1)}
              max={importProgress.total || 1}
              tone="neutral"
              size="md"
              className={styles.importProgress}
            />
          ) : null}
          {importProgress.created != null && importProgress.updated != null ? (
            <Text
              size="xs"
              c="dimmed"
              style={{ marginTop: 8, fontVariantNumeric: 'tabular-nums' }}
            >
              {importProgress.created.toLocaleString('fr-FR')} nouveaux ·{' '}
              {importProgress.updated.toLocaleString('fr-FR')} mis à jour
            </Text>
          ) : null}
          <Text size="xs" c="dimmed" style={{ marginTop: importProgress.indeterminate ? 0 : 4 }}>
            Vous pouvez quitter la page — la sync continue.
          </Text>
        </DumpSyncCard>
      ) : null}

      {showResult && job.status === 'FAILED' ? (
        <DumpSyncCard
          title="Sources GTIN — sync échouée"
          status="FAILED"
          unread
          subtitle={job.lastError ?? undefined}
          onDismiss={dismissResult}
        />
      ) : null}

      {showResult && job.status === 'COMPLETED' ? (
        <DumpSyncCard
          title="Sources GTIN — sync terminée"
          status="COMPLETED"
          unread
          onOpen={onOpenDetail}
          subtitle={
            job.lastUpserted != null
              ? job.lastCreated != null && job.lastUpdated != null
                ? `${job.lastUpserted.toLocaleString('fr-FR')} GTIN — ${job.lastCreated.toLocaleString('fr-FR')} nouveaux · ${job.lastUpdated.toLocaleString('fr-FR')} mis à jour`
                : `${job.lastUpserted.toLocaleString('fr-FR')} GTIN`
              : undefined
          }
          onDismiss={dismissResult}
        />
      ) : null}
    </Stack>
  );
}
